import React, { useEffect, useState } from "react";
import { Button, ButtonGroup, Card, Col, Container, Dropdown, Row, Spinner } from "react-bootstrap";
import WellBeingQuestions from "../../../ui/charts/WellBeingQuestions";
import ContinuumCategoryPicker from "../../../ui/charts/ContinuumCategoryPicker";
import {
  fetchAssessmentTypes,
  fetchUserCaseReportAssessments,
  fetchUserCaseReportContinuum,
  fetchUserCaseReportGoals,
  fetchUserCaseReportInteractions,
  fetchUserCaseReportTasks,
  fetchUserCaseReportWellBeing,
} from "../../../../services/httpClient";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboardCheck, faClipboardList, faClipboardQuestion } from "@fortawesome/free-solid-svg-icons";
import useApp from "../../../../utils/useApp";
import { AssessmentType } from "../../../../types/assessmenttype";
import CustomAssessments from "./CustomAssessments";
import ReportTimeInterval from "../../../reports/ui/ReportTimeInterval";
import { AssessmentLineChart } from "../../../ui/charts/AssessmentLineChart";
import DataTable from "../../../reports/ui/DataTable";
import { GoalsCompletedMetric } from "../../../reports/charts-metrics/GoalsCompletedMetric";
import { TasksCompletedMetric } from "../../../reports/charts-metrics/TasksCompletedMetric";
import { InteractionsTotalMetric } from "../../../reports/charts-metrics/InteractionsTotalMetric";
import { formatDateTimeToLocal } from "../../../../helpers/date";

interface CaseReportsState {
  getData: boolean;
}

const CaseReports = ({ getData }: CaseReportsState) => {
  const { auth }: any = useApp();
  const { id } = useParams();
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [dateInterval, setDateInterval] = useState<number>(365);
  const [interactions, setInteractions] = useState<any>();
  const [goals, setGoals] = useState<any>();
  const [tasks, setTasks] = useState<any>();
  const [selectedData, setSelectedData] = useState<any[] | null>(null);
  const [dataHeaders, setDataHeaders] = useState<any[] | null>();
  const [activeCard, setActiveCard] = useState<string>("Goals Completed");
  const [assessOneActive, setAssessOneActive] = useState<boolean>(true);
  const [assessTwoActive, setAssessTwoActive] = useState<boolean>(false);
  const [assessThreeActive, setAssessThreeActive] = useState<boolean>(false);
  const [question, setQuestion] = useState<string>();
  const [satisfaction, setSatisfaction] = useState<any>();
  const [health, setHealth] = useState<any>();
  const [purpose, setPurpose] = useState<any>();
  const [virtue, setVirtue] = useState<any>();
  const [social, setSocial] = useState<any>();
  const [stability, setStability] = useState<any>();
  const [trend, setTrend] = useState<any>();
  const [trendByQuestion, setTrendByQuestion] = useState<any>();
  const [contData, setContData] = useState<any>();
  const [wellBeingData, setWellBeingData] = useState<any>();
  const [wellBeingTitle, setWellBeingTitle] = useState<string>("Average Well Being Trend");
  const [assessmentTypes, setAssessmentTypes] = useState<AssessmentType[]>([]);
  const [assessments, setAssessments] = useState<any>();
  const [selectedType, setSelectedType] = useState<AssessmentType>();
  const [filteredAssessments, setFilteredAssessments] = useState<any>();
  const [isLoading, setIsLoading] = useState(true);

  const tableHeaders = [
    { label: "Case ID", key: "case_id", hide: true },
    { label: "Case", key: "case_name" },
    { label: "Goal ID", key: "id", hide: true },
    { label: "Goal", key: "goal_name" },
    { label: "Name", key: "subject.name" },
    { label: "Completed", key: "completed_at_formatted" },
  ];

  useEffect(() => {
    if (getData) {
      setIsLoading(true);
      const start = new Date();
      start.setDate(start.getDate() - dateInterval);
      setStartDate(start.toISOString().slice(0, 10));
      setEndDate(new Date().toISOString().slice(0, 10));
      getReportData(start.toISOString().slice(0, 10), new Date().toISOString().slice(0, 10));
    }
  }, [getData]);

  const getReportData = async (start: string, end: string) => {
    await getInteractions(start, end);
    await getGoals(start, end);
    await getTasks(start, end);
    await getContinuum();
    await getWellBeing();
    await getCustomAssessments();

    getAssessmentTypes();
    setIsLoading(false);
  };

  const getInteractions = async (start: string, end: string) => {
    await fetchUserCaseReportInteractions(start, end, id)
      .then((res: any) => {
        const data = res[0].data;
        setInteractions({
          notes: data,
        });
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getGoals = async (start: string, end: string) => {
    await fetchUserCaseReportGoals(start, end, id)
      .then((res: any) => {
        const data = res[0].data;
        data.forEach((goal: any) => {
          if (goal?.completed_at) {
            const localDateTime = formatDateTimeToLocal(goal.completed_at);
            goal.completed_at_formatted = localDateTime.date;
          }
          if (goal?.created_at) {
            const localDateTime = formatDateTimeToLocal(goal.created_at);
            goal.created_at_formatted = localDateTime.date;
          }
        });

        setGoals({
          goals: data,
        });
        setDataHeaders(tableHeaders);
        const completed = data?.filter((g: any) => g.completed_at);
        setSelectedData(completed);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getTasks = async (start: string, end: string) => {
    await fetchUserCaseReportTasks(start, end, id)
      .then((res: any) => {
        const data = res[0].data;
        data.forEach((task: any) => {
          const localDateTime = formatDateTimeToLocal(task.cm_reviewed_at);
          task.cm_reviewed_at_formatted = localDateTime.date;
        });
        setTasks({
          tasks: data,
        });
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getContinuum = async () => {
    await fetchUserCaseReportContinuum(id).then((res: any) => {
      const cont = res[0].data;
      setContData(cont);
    });
  };

  const getWellBeing = async () => {
    var s = new Date();
    s.setDate(s.getDate() - 10000);
    const start = s.toISOString().slice(0, 10);
    const end = new Date().toISOString().slice(0, 10);

    await fetchUserCaseReportWellBeing(start, end, id)
      .then((res: any) => {
        const well = res[0].data;
        calculateCategories(well);
        calculateWellBeingTrend(well);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getCustomAssessments = async () => {
    var s = new Date();
    s.setDate(s.getDate() - 10000);
    const start = s.toISOString().slice(0, 10);
    const end = new Date().toISOString().slice(0, 10);

    await fetchUserCaseReportAssessments(start, end, id)
      .then((res: any) => {
        const custom = res[0].data;
        setAssessments(custom);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getAssessmentTypes = () => {
    fetchAssessmentTypes()
      .then((res: any) => {
        var types = res[0].data;
        //gets all published and archived assessment types
        types = types.filter((t: any) => t?.is_published === 1);
        types = types.filter((t: any) => t?.organization_id == auth?.profile?.userOrgID);
        types.forEach((type: any) => {
          type.question.forEach((obj: any) => {
            obj.name = obj.question; // Rename 'question' to 'name'
          });
        });

        const formatted = types.map((t: any) => ({
          id: t.id,
          value: t.id,
          label: t.name,
          original: t,
          isDisabled: false,
          showNormal: true,
        }));
        const published = formatted.filter((t: any) => !t.original.is_archived);
        const archived = formatted.filter((t: any) => t.original.is_archived);
        const groupedTypes = [
          { label: "Published", options: published },
          { label: "Archived", options: archived },
        ];
        setAssessmentTypes(groupedTypes as any);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const calculateCategories = (data: any) => {
    var runningValue = 0;
    //Happiness and Life Satisfaction
    var working = data.filter((i: any) => i.category === "Happiness and Life Satisfaction");
    if (working?.length > 0) {
      working?.map((w: any) => {
        runningValue = w?.value + runningValue;
      });
      var value = runningValue / working?.length;
      setSatisfaction(parseFloat(value.toFixed(1)));
    } else setSatisfaction("-");

    runningValue = 0;
    //Mental and Physical Health
    working = data.filter((i: any) => i.category === "Mental and Physical Health");
    if (working?.length > 0) {
      working?.map((w: any) => {
        runningValue = w?.value + runningValue;
      });
      value = runningValue / working?.length;
      setHealth(parseFloat(value.toFixed(1)));
    } else setHealth("-");

    runningValue = 0;
    //Meaning and Purpose
    working = data.filter((i: any) => i.category === "Meaning and Purpose");
    if (working?.length > 0) {
      working?.map((w: any) => {
        runningValue = w?.value + runningValue;
      });
      value = runningValue / working?.length;
      setPurpose(parseFloat(value.toFixed(1)));
    } else setPurpose("-");

    runningValue = 0;
    //Character and Virtue
    working = data.filter((i: any) => i.category === "Character and Virtue");
    if (working?.length > 0) {
      working?.map((w: any) => {
        runningValue = w?.value + runningValue;
      });
      value = runningValue / working?.length;
      setVirtue(parseFloat(value.toFixed(1)));
    } else setVirtue("-");

    runningValue = 0;
    //Close Social Relationships
    working = data.filter((i: any) => i.category === "Close Social Relationships");
    if (working?.length > 0) {
      working?.map((w: any) => {
        runningValue = w?.value + runningValue;
      });
      value = runningValue / working?.length;
      setSocial(parseFloat(value.toFixed(1)));
    } else setSocial("-");

    runningValue = 0;
    //Financial and Material Stability
    working = data.filter((i: any) => i.category === "Financial and Material Stability");
    if (working?.length > 0) {
      working?.map((w: any) => {
        runningValue = w?.value + runningValue;
      });
      value = runningValue / working?.length;
      setStability(parseFloat(value.toFixed(1)));
    } else setStability("-");
  };

  const calculateWellBeingTrend = (data: any[]) => {
    const groupedData: { [date: string]: { sum: number; count: number } } = data.reduce((acc: any, obj: any) => {
      const date = obj.assessment_at;
      const value = obj.value;
      if (!acc[date]) {
        acc[date] = { sum: 0, count: 0 };
      }
      acc[date].sum += value;
      acc[date].count++;
      return acc;
    }, {});

    // Calculate average for each group
    const averagedData = Object.entries(groupedData).map(([date, { sum, count }]) => ({
      date,
      score: sum / count,
    }));

    setTrend(averagedData);
    setQuestion("zero");

    // Example usage
    const groupedData2 = data.reduce((acc: any, obj: any) => {
      const key = obj.key;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push({ date: obj.assessment_at, score: obj.value });
      return acc;
    }, {});

    setTrendByQuestion(groupedData2);
  };

  useEffect(() => {
    switch (question) {
      case "one":
        setWellBeingData(trendByQuestion[1]);
        setWellBeingTitle("Question One Trend");
        break;
      case "two":
        setWellBeingData(trendByQuestion[2]);
        setWellBeingTitle("Question Two Trend");
        break;
      case "three":
        setWellBeingData(trendByQuestion[3]);
        setWellBeingTitle("Question Three Trend");
        break;
      case "four":
        setWellBeingData(trendByQuestion[4]);
        setWellBeingTitle("Question Four Trend");
        break;
      case "five":
        setWellBeingData(trendByQuestion[5]);
        setWellBeingTitle("Question Five Trend");
        break;
      case "six":
        setWellBeingData(trendByQuestion[6]);
        setWellBeingTitle("Question Six Trend");
        break;
      case "seven":
        setWellBeingData(trendByQuestion[7]);
        setWellBeingTitle("Question Seven Trend");
        break;
      case "eight":
        setWellBeingData(trendByQuestion[8]);
        setWellBeingTitle("Question Eight Trend");
        break;
      case "nine":
        setWellBeingData(trendByQuestion[9]);
        setWellBeingTitle("Question Nine Trend");
        break;
      case "ten":
        setWellBeingData(trendByQuestion[10]);
        setWellBeingTitle("Question Ten Trend");
        break;
      case "eleven":
        setWellBeingData(trendByQuestion[11]);
        setWellBeingTitle("Question Eleven Trend");
        break;
      case "twelve":
        setWellBeingData(trendByQuestion[12]);
        setWellBeingTitle("Question Twelve Trend");
        break;
      default:
        setWellBeingData(trend);
        setWellBeingTitle("Average Well Being Trend");
        break;
    }
  }, [question]);

  const updateAssessmentSelection = (type: string) => {
    switch (type) {
      case "wellbeing":
        setAssessOneActive(true);
        setAssessTwoActive(false);
        setAssessThreeActive(false);
        break;
      case "continuum":
        setAssessOneActive(false);
        setAssessTwoActive(true);
        setAssessThreeActive(false);
        break;
      case "custom":
        setAssessOneActive(false);
        setAssessTwoActive(false);
        setAssessThreeActive(true);
        break;
      default:
        setAssessOneActive(true);
        setAssessTwoActive(false);
        setAssessThreeActive(false);
        break;
    }
  };

  const updateCustomAssessmentSelection = (type: any) => {
    updateAssessmentSelection("custom");
    const filtered = assessments.filter((a: any) => a.assessmenttype_id === type.id);
    setFilteredAssessments(filtered);
    setSelectedType(type);
  };

  const onClickCard = (cardId: string) => {
    setActiveCard((prev) => (prev === cardId ? "" : cardId));
  };

  return (
    <>
      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <Row>
            <Col>
              <h4 className="mb-3 app-header-text">Interactions and Goals</h4>
            </Col>
          </Row>
          <ReportTimeInterval
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            dateInterval={dateInterval}
            setDateInterval={setDateInterval}
            intervalOne={30}
            intervalOneText={"30 Days"}
            intervalTwo={90}
            intervalTwoText={"90 Days"}
            intervalThree={180}
            intervalThreeText={"180 Days"}
            intervalFour={365}
            intervalFourText={"Last Year"}
            onClickRefresh={getReportData}
          />
          <Row className="mt-3">
            <Col>
              <InteractionsTotalMetric title={"All Interactions"} data={interactions} readOnly={true} />
            </Col>
            <Col>
              <GoalsCompletedMetric
                data={goals}
                readOnly={false}
                setSelectedData={setSelectedData}
                setDataHeaders={setDataHeaders}
                active={activeCard === "Goals Completed"}
                onHandleClick={() => onClickCard("Goals Completed")}
              />
            </Col>
            <Col>
              <TasksCompletedMetric
                data={tasks}
                readOnly={false}
                setSelectedData={setSelectedData}
                setDataHeaders={setDataHeaders}
                active={activeCard === "Tasks Completed"}
                onHandleClick={() => onClickCard("Tasks Completed")}
              />
            </Col>
          </Row>
          <div className="pdf-page">
            {activeCard && selectedData && (
              <DataTable tableHeadings={dataHeaders} tableData={selectedData} title={activeCard} overrideCSS="mb-0" />
            )}
          </div>
          <hr />
          <Row className="mb-3">
            <Col xs={3}>
              <h4 className="app-header-text">Assessments</h4>
            </Col>
            <Col xs={3} className="d-grid">
              <Button
                className={assessOneActive ? "app-primary-color button" : "app-primary-color-inverse button"}
                onClick={() => updateAssessmentSelection("wellbeing")}
              >
                <FontAwesomeIcon icon={faClipboardList} className={"me-2"} />
                {"Well Being"}
              </Button>
            </Col>
            <Col xs={3} className="d-grid">
              <Button
                className={assessTwoActive ? "app-primary-color button" : "app-primary-color-inverse button"}
                onClick={(e) => updateAssessmentSelection("continuum")}
              >
                <FontAwesomeIcon icon={faClipboardCheck} className={"me-2"} />
                {"Continuum"}
              </Button>
            </Col>
            <Col xs={3} className="d-grid">
              <Dropdown as={ButtonGroup}>
                <Dropdown.Toggle
                  id="dropdown-custom-1"
                  className={assessThreeActive ? "app-primary-color button" : "app-primary-color-inverse button"}
                >
                  <FontAwesomeIcon icon={faClipboardQuestion} className="me-2" />
                  Custom
                </Dropdown.Toggle>
                <Dropdown.Menu className="super-colors">
                  {assessmentTypes.map((type: any, index) => (
                    <React.Fragment key={index}>
                      <Dropdown.ItemText
                        className={"text-uppercase fst-normal"}
                        style={{ fontSize: "75%", fontWeight: 500, color: "#999" }}
                      >
                        {type.label}
                      </Dropdown.ItemText>
                      {type?.options?.map((option: any) => (
                        <Dropdown.Item key={option.key} eventKey={option.id} onClick={() => updateCustomAssessmentSelection(option)}>
                          {option.label}
                        </Dropdown.Item>
                      ))}
                    </React.Fragment>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>
          {assessOneActive && (
            <>
              <Row className="justify-content-center gap-3 mb-3">
                <Col className="col-5">
                  <Card className="bg-light w-100 h-100">
                    <Card.Body className="d-flex flex-column">
                      <Row className="text-center">
                        <Col xs={8} lg={9} className="d-flex align-items-center">
                          <h5 className="app-orange-text">{"Happiness and Life Satisfaction"}</h5>
                        </Col>
                        <Col xs={4} lg={3} className="d-flex align-items-center">
                          <h2 className="text-muted text-end">{satisfaction}</h2>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
                <Col className="col-5">
                  <Card className="bg-light w-100 h-100">
                    <Card.Body className="d-flex flex-column">
                      <Row className="text-center">
                        <Col xs={8} lg={9} className="d-flex align-items-center">
                          <h5 className="app-orange-text">{"Mental and Physical Health"}</h5>
                        </Col>
                        <Col xs={4} lg={3} className="d-flex align-items-center">
                          <h2 className="text-muted text-end">{health}</h2>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
                <Col className="col-5">
                  <Card className="bg-light w-100 h-100">
                    <Card.Body className="d-flex flex-column">
                      <Row className="text-center">
                        <Col xs={8} lg={9} className="d-flex align-items-center">
                          <h5 className="app-orange-text">{"Meaning and Purpose"}</h5>
                        </Col>
                        <Col xs={4} lg={3} className="d-flex align-items-center">
                          <h2 className="text-muted text-end">{purpose}</h2>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
                <Col className="col-5">
                  <Card className="bg-light w-100 h-100">
                    <Card.Body className="d-flex flex-column">
                      <Row className="text-center">
                        <Col xs={8} lg={9} className="d-flex align-items-center">
                          <h5 className="app-orange-text">{"Character and Virtue"}</h5>
                        </Col>
                        <Col xs={4} lg={3} className="d-flex align-items-center">
                          <h2 className="text-muted text-end">{virtue}</h2>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
                <Col className="col-5">
                  <Card className="bg-light w-100 h-100">
                    <Card.Body className="d-flex flex-column">
                      <Row className="text-center">
                        <Col xs={8} lg={9} className="d-flex align-items-center">
                          <h5 className="app-orange-text">{"Close Social Relationships"}</h5>
                        </Col>
                        <Col xs={4} lg={3} className="d-flex align-items-center">
                          <h2 className="text-muted text-end">{social}</h2>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
                <Col className="col-5">
                  <Card className="bg-light w-100 h-100">
                    <Card.Body className="d-flex flex-column">
                      <Row className="text-center">
                        <Col xs={8} lg={9} className="d-flex align-items-center">
                          <h5 className="app-orange-text">{"Financial and Material Stability"}</h5>
                        </Col>
                        <Col xs={4} lg={3} className="d-flex align-items-center">
                          <h2 className="text-muted text-end">{stability}</h2>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <WellBeingQuestions question={question} setQuestion={setQuestion} />
              <Row className="mb-5">
                <Col></Col>
                <Col className="col-8">
                  <AssessmentLineChart titleText={wellBeingTitle} data={wellBeingData} />
                </Col>
                <Col></Col>
              </Row>
            </>
          )}
          {assessTwoActive && (
            <>
              <Row className="mb-5">
                <ContinuumCategoryPicker data={contData} />
              </Row>
            </>
          )}
          {assessThreeActive && <CustomAssessments assessments={filteredAssessments} assessmentType={selectedType} />}
        </>
      )}
    </>
  );
};

export default CaseReports;
